import {
  Button,
  Checkbox,
  CheckboxGroup,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Stack,
} from '@chakra-ui/react';
import { useState } from 'react';
import type { CreateGroupCampaignPayload } from 'services/interfaces/storeGroup';

interface CreateGroupCampaignModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (payload: CreateGroupCampaignPayload) => void;
  submitting: boolean;
}

const CreateGroupCampaignModal: React.FC<CreateGroupCampaignModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  submitting,
}) => {
  const [name, setName] = useState('');
  const [cashbackPercentage, setCashbackPercentage] = useState(5);
  const [minimumOrderValue, setMinimumOrderValue] = useState(0);
  const [minimumRedeemOrderValue, setMinimumRedeemOrderValue] = useState(0);
  const [startAt, setStartAt] = useState('');
  const [endAt, setEndAt] = useState('');
  const [channels, setChannels] = useState<string[]>(['PDV']);

  const reset = () => {
    setName('');
    setCashbackPercentage(5);
    setMinimumOrderValue(0);
    setMinimumRedeemOrderValue(0);
    setStartAt('');
    setEndAt('');
    setChannels(['PDV']);
  };

  const handleClose = () => {
    if (submitting) return;
    reset();
    onClose();
  };

  const canSubmit =
    name.trim().length > 0 && startAt.trim().length > 0 && channels.length > 0;

  const handleSubmit = () => {
    if (!canSubmit) return;
    onConfirm({
      name: name.trim(),
      cashback_percentage: cashbackPercentage,
      minimum_order_value: Math.round(minimumOrderValue * 100),
      minimum_redeem_order_value: Math.round(minimumRedeemOrderValue * 100),
      start_at: new Date(startAt).toISOString(),
      end_at: endAt ? new Date(endAt).toISOString() : undefined,
      channels,
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} isCentered size="lg">
      <ModalOverlay />
      <ModalContent mx={3}>
        <ModalHeader>Criar campanha de cashback do grupo</ModalHeader>
        <ModalCloseButton isDisabled={submitting} />
        <ModalBody>
          <Stack spacing={4}>
            <FormControl isRequired>
              <FormLabel>Nome da campanha</FormLabel>
              <Input
                value={name}
                isDisabled={submitting}
                onChange={(e) => setName(e.target.value)}
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel>Percentual de cashback (%)</FormLabel>
              <NumberInput
                value={cashbackPercentage}
                min={0}
                max={100}
                isDisabled={submitting}
                onChange={(_, v) =>
                  setCashbackPercentage(Number.isNaN(v) ? 0 : v)
                }
              >
                <NumberInputField />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>
            </FormControl>

            <HStack align="start">
              <FormControl>
                <FormLabel>Pedido mínimo p/ acumular (R$)</FormLabel>
                <NumberInput
                  value={minimumOrderValue}
                  min={0}
                  isDisabled={submitting}
                  onChange={(_, v) =>
                    setMinimumOrderValue(Number.isNaN(v) ? 0 : v)
                  }
                >
                  <NumberInputField />
                </NumberInput>
              </FormControl>
              <FormControl>
                <FormLabel>Pedido mínimo p/ resgatar (R$)</FormLabel>
                <NumberInput
                  value={minimumRedeemOrderValue}
                  min={0}
                  isDisabled={submitting}
                  onChange={(_, v) =>
                    setMinimumRedeemOrderValue(Number.isNaN(v) ? 0 : v)
                  }
                >
                  <NumberInputField />
                </NumberInput>
              </FormControl>
            </HStack>

            <HStack align="start">
              <FormControl isRequired>
                <FormLabel>Início</FormLabel>
                <Input
                  type="date"
                  value={startAt}
                  isDisabled={submitting}
                  onChange={(e) => setStartAt(e.target.value)}
                />
              </FormControl>
              <FormControl>
                <FormLabel>Fim (opcional)</FormLabel>
                <Input
                  type="date"
                  value={endAt}
                  isDisabled={submitting}
                  onChange={(e) => setEndAt(e.target.value)}
                />
              </FormControl>
            </HStack>

            <FormControl isRequired>
              <FormLabel>Canais</FormLabel>
              <CheckboxGroup
                value={channels}
                onChange={(v) => setChannels(v as string[])}
              >
                <HStack>
                  <Checkbox value="PDV" isDisabled={submitting}>
                    PDV
                  </Checkbox>
                  <Checkbox value="DELIVERY" isDisabled={submitting}>
                    Delivery
                  </Checkbox>
                </HStack>
              </CheckboxGroup>
            </FormControl>
          </Stack>
        </ModalBody>
        <ModalFooter gap={2}>
          <Button variant="ghost" onClick={handleClose} isDisabled={submitting}>
            Cancelar
          </Button>
          <Button
            colorScheme="yooga"
            onClick={handleSubmit}
            isLoading={submitting}
            isDisabled={!canSubmit}
          >
            Criar campanha
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default CreateGroupCampaignModal;
